import React, { useEffect, useMemo, useState } from 'react';
import { getLocale, useIntl } from 'umi';
import CardCommon from '@op/components/CardCommon';
import EChartCommon, { getAxisPointer } from '@op/components/EChartCommon';
import { lineIcon, barSIcon, pieIcon, tableIcon } from '@op/constants/chart';
import { chartAllColors } from '@/pages/opinion/constants';
import {
  formatChartParamsDate,
  listTimeTitleFunc,
} from '@op/components/ChartCommon/formatter';
import { getPercentageInfo, mathFormat } from '@op/utils';
import { reviewsFormat, sentimentFormat, yAxisFormat } from '@op/utils/value';
import {
  formatTimeXAxisText,
  getChartPosition,
  getXAxisAxisLabel,
} from '@op/utils/chart';
import './index.scss';
import { useOverviewChannelOrLanguageInfo } from '../../hooks/channel';
import { formatTimeToShow } from '@op/utils/time';
import ChannelLanguageTable from '../ChannelLanguageTable';
import { useDownloadParams } from '@op/hooks/useDownloadParams';
import { dealWithOthersName } from '@op/utils/languageValue';
import { channelMentionSentimentTabType } from '../../constants';
import DetailWrapper from '@op/pages/Detail/component/DetailWrapper';
import { detailPageTips } from '../../utils';
import SelectDropdown from '../../component/SelectDropdown';

const dropdownOptions = [
  { key: 'mentions', labelId: 'Mentions' },
  { key: 'avg_sentiment', labelId: 'Sentiment' },
];
/**
 * TODO: 其他系统使用
 * @param param0
 * @returns
 */
export default function LanguageChart({
  filter,
  titleClickFunc,
  tab,
  isDetail,
  updateDetailFilter,
  defaultSelected,
  defaultChartType,
  detailPage,
}: any) {
  const { formatMessage } = useIntl();
  const { download, downloading } = useDownloadParams();
  const [chartType, setChartType] = useState(
    defaultChartType || 'sentiment-s-bar',
  );
  const [valueKey, setValueKey] = useState('mentions');
  const eChartsRef = React.useRef(null as any);

  const params = useMemo(() => {
    return {
      ...filter,
      need_link: true,
      channel_type: channelMentionSentimentTabType[tab] || '',
      ...(tab === 'news' ? { types: ['total', 'slice'] } : {}),
    };
  }, [filter, tab]);

  const { loading, chartData, empty, tableData } =
    useOverviewChannelOrLanguageInfo({
      filter: params,
      type: 'language',
    });

  const initMentions = (chartType: string) => {
    if (chartType === 'pie') {
      return createPieChartOptions(); // Separate function to create pie chart options
    }

    if (chartType === 'line') {
      return createLineChartOptions(); // Separate function to create line chart options
    }

    return createBarChartOptions(); // Separate function to create bar chart options
  };

  const measureTextWidth = (texts: string[]) => {
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    if (!context) {
      return [];
    }
    context.font = '12px Roboto';
    return texts?.map(
      (text) =>
        context.measureText(dealWithOthersName(text, 'language')).width || 0,
    );
  };

  const createPieChartOptions = () => {
    const { legendData, data, mentionsTotal } = chartData.pie;

    return {
      legend: {
        data: legendData,
        formatter: (params: string) => dealWithOthersName(params, 'language'),
        ...(defaultSelected ? { selected: defaultSelected } : {}),
      },
      tooltip: {
        borderWidth: 0,
        borderRadius: 6,
        padding: 0,
        formatter: (params: any) =>
          formatChartParamsDate({
            params,
            listTitleFunc: (item) => item.name,
            textShowFun: (marker: string, seriesName: string) =>
              `<span class='echart-tooltip'><span class="chart-common-formatter-marker">${marker}</span>&nbsp;${dealWithOthersName(
                seriesName,
                'language',
              )}</span>`,
            valueFormat: (value) => {
              const percent = getPercentageInfo(
                params?.value,
                mentionsTotal,
                2,
              );
              return `${isNaN(value) ? '-' : reviewsFormat(value)}(${percent})`;
            },
            tablesConfigFunc: (records: any) => {
              const columns = [
                {
                  key: 'text',
                  header: formatTimeToShow(filter),
                },
                {
                  key: 'value',
                  align: 'right',
                  weight: 'bold',
                  header: formatMessage({ id: 'Mentions' }),
                },
              ];

              const finalRecords = detailPageTips(
                records,
                formatMessage,
                detailPage,
              );
              return [
                {
                  showHeader: true,
                  records: finalRecords,
                  columns,
                },
              ];
            },
          }),
      },
      title: [
        {
          text: `{name|${mathFormat(mentionsTotal)}}`,
          top: '43%',
          left: 'center',
          textStyle: {
            rich: {
              name: {
                fontSize: 24,
                fontFamily: 'num-m',
                color: '#000000',
                padding: [10, 0],
              },
            },
          },
        },
      ],
      series: [
        {
          data,
          label: {
            color: '#262832',
            alignTo: 'edge',
            formatter: (params: any) => {
              const percent = getPercentageInfo(
                params?.value,
                mentionsTotal,
                2,
              );
              return `{value| ${dealWithOthersName(
                params.name,
                'language',
              )}}: {a|${mathFormat(params.value)}${
                params?.value ? `(${percent})` : ''
              }}\n{hr|}\n{a|}`;
            },
            edgeDistance: 10,
            rich: {
              a: {
                // color: '#999',
                lineHeight: 20, // 设置最后一行空数据高度，为了能让延长线与hr线对接起来
                align: 'center',
              },
              hr: {
                // 设置hr是为了让中间线能够自适应长度
                borderColor: 'auto', // hr的颜色为auto时候会主动显示颜色的
                width: '105%',
                borderWidth: 0.5,
                height: 0.5,
              },
              per: {
                // 用百分比数据来调整下数字位置，显的好看些。如果不设置，formatter最后一行的空数据就不需要
                padding: [4, 0],
              },
            },
          },
          labelLayout: (params: any) => {
            const isLeft =
              params.labelRect.x <
              eChartsRef?.current?.getEchartsInstance?.()?.getWidth() / 2;
            const points = params.labelLinePoints;
            // Update the end point.
            points[2][0] = isLeft
              ? params.labelRect.x
              : params.labelRect.x + params.labelRect.width;
            return {
              labelLinePoints: points,
            };
          },
          emphasis: {
            focus: 'self',
            label: {
              opacity: 0.2,
            },
            labelLine: {
              lineStyle: {
                opacity: 0.2,
              },
            },
          },
          blur: {
            label: { opacity: 0.2 },
            itemStyle: {
              opacity: 0.2,
            },
            labelLine: {
              lineStyle: {
                opacity: 0.2,
              },
            },
          },
        },
      ],
      graphic: [
        {
          type: 'text',
          left: 'center',
          top: '34%',
          style: {
            fill: '#444444',
            fontSize: 12,
          },
        },
      ],
    };
  };

  // Function to create line chart options
  const createLineChartOptions = () => {
    const { newArr, timeArr: xAxisData, legendData } = chartData.timeline;

    return {
      grid: {
        top: 20,
        contentLabel: true,
        left: 0,
        right: 5,
      },
      color: chartAllColors,
      tooltip: {
        trigger: 'axis',
        appendToBody: false,
        confine: false,
        position: (pos: any, params: any, dom: any, rect: any, size: any) =>
          getChartPosition(pos, params, dom, rect, size, -5),
        formatter: (params: any) => {
          let currentDateTotal = 0;
          params?.forEach((item) => (currentDateTotal += item?.value));
          const sumText = formatMessage({ id: 'Mentions' });
          // if (timelineTotal) {
          //   currentDateTotal =
          //     timelineTotal[params[0]?.name] || currentDateTotal;
          //   sumText = `${sumText} (${formatMessage({
          //     id: 'Removed duplicate reviews',
          //   })})`;
          // }
          return formatChartParamsDate({
            params,
            total: currentDateTotal,
            isSort: true,
            valueFormat: (value: number) => reviewsFormat(value, 2),
            textShowFun: (marker: string, seriesName: string) =>
              `<span class='echart-tooltip'><span class="chart-common-formatter-marker">${marker}</span>&nbsp;${dealWithOthersName(
                seriesName,
                'language',
              )}</span>`,
            dealwithRecords(records, total) {
              const newRecords: Array<any> = records.map((item) => {
                const share = getPercentageInfo(item.originValue, total, 2);
                return {
                  ...item,
                  value: `${item.value} ${
                    item.originValue ? `(${share})` : ''
                  }`,
                };
              });

              newRecords.unshift({
                text: sumText,
                value: reviewsFormat(total),
                originValue: total,
                originText: '',
              });
              const finalRecords = detailPageTips(
                newRecords,
                formatMessage,
                detailPage,
              );
              return finalRecords.filter((item) => Boolean(item));
            },
            tablesConfigFunc: (records: any) => {
              const columns = [
                {
                  key: 'text',
                  header: listTimeTitleFunc(params[0]?.name, filter.date_type),
                },
                {
                  key: 'value',
                  align: 'right',
                  weight: 'bold',
                  header: '',
                },
              ];

              return [
                {
                  showHeader: true,
                  records,
                  columns,
                },
              ];
            },
          });
        },
      },
      legend: {
        data: legendData,
        formatter: (params: string) => dealWithOthersName(params, 'language'),
        ...(defaultSelected ? { selected: defaultSelected } : {}),
      },
      axisPointer: getAxisPointer((data) =>
        formatTimeXAxisText(data.value, filter.date_type),
      ),
      xAxis: {
        type: 'category',
        axisLabel: getXAxisAxisLabel(filter),
        data: xAxisData,
        boundaryGap: ['20%', 0],
      },
      yAxis: {
        type: 'value',
        axisLabel: {
          formatter: (value: any) => yAxisFormat(value),
        },
      },
      series: newArr?.map((val: any, index: number) => ({
        ...val,
        stack: `Total${index}`,
        // showSymbol: false,
        // symbol: 'circle',
        // symbolSize: 8,
        // emphasis: {
        //   showSymbol: true,
        // },
      })),
    };
  };

  // Function to create bar chart options
  const createBarChartOptions = () => {
    const {
      dataY,
      negativeMentions,
      neutralMentions,
      positiveMentions,
      total,
      sentimentList,
    } = chartData.bar;
    const isNegative =
      negativeMentions?.filter((val) => Math.abs(val.value) > 0)?.length > 0;
    const isPositive =
      positiveMentions?.filter((val) => Math.abs(val.value) > 0)?.length > 0;
    let selected = {
      Negative: true,
      Neutral: false,
      Positive: true,
    };
    if (!isNegative && !isPositive) {
      selected = {
        Negative: true,
        Neutral: true,
        Positive: true,
      };
    }
    const maxLabelWidth = Math.max(...(measureTextWidth(dataY) || [])) || 60;
    const rounded = Math.round(maxLabelWidth / 10) * 10;
    const width = (rounded > maxLabelWidth ? rounded : rounded + 10) + 10;

    return {
      grid: {
        left: 0,
        right: 0,
        contentLabel: true,
      },
      tooltip: {
        extraCssText: 'z-index:8',
        confine: true,
        trigger: 'axis',
        formatter: (params: any) => {
          const currentDateTotal = total[params[0].dataIndex];
          const sumText = formatMessage({ id: 'Total' });
          return formatChartParamsDate({
            params,
            total: currentDateTotal,
            // isSort: true,
            beforeSort: false,
            valueFormat: (value: number) => reviewsFormat(Math.abs(value), 2),
            listTitleFunc: (item) => formatMessage({ id: item.seriesName }),
            dealwithRecords(records, total) {
              const newRecords: Array<any> = records.map((item) => {
                // if (!item.originValue) return null;
                const share = getPercentageInfo(
                  Math.abs(item.originValue || 0),
                  total,
                  2,
                );
                return {
                  ...item,
                  share: share === '-' || share === '0%' ? '-' : share,
                };
              });
              newRecords.reverse();
              newRecords.unshift({
                text: sumText,
                value: reviewsFormat(total),
                share: '100%',
              });
              const finalRecords = detailPageTips(
                newRecords,
                formatMessage,
                detailPage,
              );
              return finalRecords.filter((item) => Boolean(item));
            },
            tablesConfigFunc: (records: any) => {
              const columns = [
                {
                  key: 'text',
                  header: `<span class="echart-tooltip">${dealWithOthersName(
                    params[0]?.name,
                    'language',
                  )}<span>`,
                },
                {
                  key: 'value',
                  align: 'right',
                  weight: 'bold',
                  header: formatMessage({ id: 'Mentions' }),
                },
                {
                  key: 'share',
                  align: 'right',
                  weight: 'bold',
                  header: formatMessage({ id: 'Share-1' }),
                },
              ];

              return [
                {
                  showHeader: true,
                  records,
                  columns,
                },
              ];
            },
          });
        },
        // triggerOn: isDetail ? 'click' : 'mousemove',
        axisPointer: {
          type: 'shadow',
        },
      },
      legend: {
        data: [
          {
            name: 'Negative',
            itemStyle: { color: '#F17C86' },
          },
          {
            name: 'Neutral',
            itemStyle: { color: '#6E7794' },
          },
          {
            name: 'Positive',
            itemStyle: { color: '#69D19C' },
          },
        ],
        selected: defaultSelected ?? selected,
        formatter(name: string) {
          return formatMessage({ id: name });
        },
      },
      xAxis: {
        type: 'value',
        splitLine: {
          show: true,
          lineStyle: { color: '#E5E5E5', type: 'dashed' },
        },
        axisLine: {
          show: true,
          lineStyle: { color: '#E5E5E5' },
        },
        axisLabel: {
          formatter: (value: number) => yAxisFormat(Math.abs(value)), // 负值转成正值
        },
        boundaryGap: ['10%', 0],
      },
      // axisPointer: { type: 'shadow' },
      yAxis: [
        {
          type: 'category',
          position: 'left',
          axisLabel: {
            interval: 0,
            show: true,
            formatter: (value: any) =>
              `{channel|${dealWithOthersName(value, 'language')}}`,
            rich: {
              channel: {
                align: 'left',
              },
            },
            overflow: 'break',
            width,
            fontWeight: 400,
            fontSize: 12,
            lineHeight: 16,
            padding: [0, 0, 0, 0],
          },
          splitLine: {
            show: false,
            lineStyle: { color: '#E5E5E5', type: 'dashed' },
          },
          axisLine: {
            show: false,
          },
          axisTick: {
            show: false,
          },
          data: dataY,
        },
        {
          type: 'category',
          name: 'Mentions',
          position: 'right',
          data: valueKey === 'mentions' ? total : sentimentList,
          axisLabel: {
            formatter: (value: number) =>
              valueKey === 'mentions'
                ? `{value|${reviewsFormat(value)}}`
                : `{value|${sentimentFormat(value)}}`,
            show: true,
            width: 40,
            rich: {
              value: {
                align: 'right',
              },
            },
            fontWeight: 400,
            fontSize: 12,
            lineHeight: 16,
            padding: [0, 0, 0, 0],
          },
          splitLine: {
            show: false,
            lineStyle: { color: '#E5E5E5', type: 'dashed' },
          },
          axisLine: {
            show: false,
          },
          axisTick: {
            show: false,
          },
        },
      ],
      series: [
        {
          name: 'Negative',
          type: 'bar',
          stack: 'total',
          color: '#F17C86',
          barMaxWidth: 30,
          label: {
            show: true,
            position: 'left',
            formatter: (params: { value: number; dataIndex: number }) => {
              const value = Math.abs(params.value);
              if (value) {
                return getPercentageInfo(value, total[params.dataIndex], 2);
              }
              return '';
            },
          },
          emphasis: {
            disable: true,
          },
          data: negativeMentions,
        },
        {
          name: 'Neutral',
          type: 'bar',
          stack: 'total',
          color: '#6E7794',
          barMaxWidth: 30,
          label: {
            show: false,
          },
          emphasis: {
            disable: true,
          },
          data: neutralMentions,
        },
        {
          name: 'Positive',
          type: 'bar',
          stack: 'total',
          color: '#69D19C',
          barMaxWidth: 30,
          label: {
            show: false,
          },
          emphasis: {
            disable: true,
          },
          data: positiveMentions,
        },
      ],
    };
  };

  const options = useMemo(
    () => initMentions(chartType),
    [chartData, chartType, formatMessage, valueKey],
  );

  const optionsProps = useMemo(() => {
    if (chartType !== 'sentiment-s-bar') {
      return {
        type: chartType,
      };
    }
    return {};
  }, [chartType]);

  const height =
    chartType === 'sentiment-s-bar' && !loading && !empty ? 324 : 342;

  function getEventProps(): any {
    const key = filter?.logic === 'or' ? 'or_drill_language' : 'language';
    return {
      key: 'KeyMetricsLanguage',
      defaultFilter: { ...(filter || {}), dataType: 'language' },
      defaultProps: {
        titleClickFunc,
        tab,
        isDetail,
        updateDetailFilter,
        defaultChartType: chartType,
        detailPage,
      },
      paramsKey: {
        'sentiment-s-bar': {
          [key]: 'name',
          sentiment: 'seriesName',
        },
        line: {
          start_time: 'name',
          end_time: 'name',
          [key]: 'seriesName',
        },
        pie: {
          [key]: 'name',
        },
        table: {
          [key]: 'name',
        },
      },
      chartType,
      specialParamsKey: {
        'sentiment-s-bar': {
          [key]: 'selected',
        },
      },
    };
  }

  return (
    <CardCommon
      // contentCard
      className={'dbo-mention-sentiment-channel'}
      operations={[barSIcon, lineIcon, pieIcon, tableIcon]}
      hasTitleArrow
      loading={loading}
      empty={empty}
      loadingHeight={height}
      emptyHeight={height}
      bodyStyle={{ minHeight: 418 }}
      title={formatMessage({ id: 'Language' })}
      handleTitleOnClick={titleClickFunc}
      defaultOperation={chartType}
      hasDownload={!empty}
      downloadLoading={downloading}
      handleOperationsOnClick={(item) => {
        setChartType(item?.symbol || 'sentiment-s-bar');
      }}
      logBtnId="KMLG"
      handleDownloadOnClick={() => {
        download({
          key: 'KeyMetricsLanguage',
          fileName: 'Key Metrics Language',
          filter,
          auxiliaryParams: {},
          data: tableData,
        });
      }}
    >
      <DetailWrapper
        eventProps={getEventProps()}
        detailPage={detailPage}
        isDetail={isDetail}
        updateDetailFilter={updateDetailFilter}
        formatParamsKey={(value, key) => {
          if (value === 'Others' && ['language', "or_drill_language"].includes(key) ) {
            const { dataY } = chartData.bar;
            return {
              except_language:
                dataY?.filter((item: string) => item !== 'Others') || [],
              [key]: [],
            };
          }
          return value;
        }}
      >
        {({ eventsDict, tableWrapper, onChartReady }) => (
          <>
            {chartType === 'table' ? (
              <ChannelLanguageTable
                filter={filter}
                header={'Language'}
                tableWrapper={tableWrapper}
                records={tableData}
                eventsDict={eventsDict}
              />
            ) : (
              <EChartCommon
                options={options}
                rightTitle={
                  chartType === 'sentiment-s-bar' ? (
                    <SelectDropdown
                      value={valueKey}
                      onChange={setValueKey}
                      options={dropdownOptions}
                      appearance="link"
                    />
                  ) : (
                    ''
                  )
                }
                optionsProps={optionsProps}
                ref={eChartsRef}
                height={height}
                eventsDict={eventsDict}
                onChartReady={onChartReady}
              />
            )}
          </>
        )}
      </DetailWrapper>
    </CardCommon>
  );
}
